import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { editCaseCreationWithupload } from "services/caseServices";
import { CaseCreation } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm } from "utils/helper";

type Props = {
  caseData: CaseCreation;
  onClose: (refetch: boolean) => void;
};

const PerformanceForm: FC<Props> = ({ caseData, onClose }) => {
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);

  const [formState, setFormState] = useState<{
    evaluation_date: string;
    work_speed_evaluation: number;
    work_quality_evaluation: number;
    work_load_evaluation: number;
  }>({
    evaluation_date: "",
    work_speed_evaluation: 0,
    work_quality_evaluation: 0,
    work_load_evaluation: 0,
  });

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...(prevState ?? {}),
      [name]: value,
    }));
  };

  const hasPerformance = caseData?.evaluation_date;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-[600px]">
        {hasPerformance ? (
          <>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Performance Evaluation
            </h2>
            <div className="flex flex-col gap-2">
              <div>
                <span className="font-semibold text-[#AA232F]">
                  Work Speed Evaluation:
                </span>
                <span className="ml-2 text-gray-700">
                  {caseData.work_speed_evaluation}
                </span>
              </div>
              <div>
                <span className="font-semibold text-[#AA232F]">
                  Work Quality Evaluation:
                </span>
                <span className="ml-2 text-gray-700">
                  {caseData.work_quality_evaluation}
                </span>
              </div>
              <div>
                <span className="font-semibold text-[#AA232F]">
                  Work Load Evaluation:
                </span>
                <span className="ml-2 text-gray-700">
                  {caseData.work_load_evaluation}
                </span>
              </div>
              <div>
                <span className="font-semibold text-[#AA232F]">
                  Evaluation Date:
                </span>
                <span className="ml-2 text-gray-700">
                  {caseData.evaluation_date
                    ? new Date(caseData.evaluation_date).toLocaleDateString()
                    : ""}
                </span>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded w-full hover:bg-gray-300"
                onClick={() => {
                  onClose(false);
                }}
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-gray-800">
              Evaluate Performance
            </h2>
            <p className="text-xs text-gray-500">
              Are you sure you want to evaluate the performance of this case?
            </p>
            {/* ...existing form code... */}
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Work Speed Evaluation
              </label>
              <input
                type="number"
                placeholder="Enter work speed evaluation."
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="work_speed_evaluation"
                min={0}
                max={100}
                step={0.01}
                value={formState.work_speed_evaluation}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Work Quality Evaluation
              </label>
              <input
                type="number"
                placeholder="Enter work quality evaluation."
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="work_quality_evaluation"
                min={0}
                max={100}
                step={0.01}
                value={formState.work_quality_evaluation}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Work Load Evaluation
              </label>
              <input
                type="number"
                placeholder="Enter work load evaluation."
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="work_load_evaluation"
                min={0}
                max={100}
                step={0.01}
                value={formState.work_load_evaluation}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Date
              </label>
              <input
                type="date"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="evaluation_date"
                value={
                  formState.evaluation_date
                    ? new Date(formState.evaluation_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                min={new Date().toISOString().split("T")[0]}
                onChange={handleChange}
              />
            </div>
            <div className="mt-4 flex justify-between gap-2">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded w-full hover:bg-gray-300"
                onClick={() => {
                  onClose(false);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`border border-[#E90101] bg-[#E90101] px-10 py-2 text-white 
                            font-[Poppins] cursor-pointer font-[500] w-full
                            text-base rounded-md hover:bg-white hover:text-[#E90101]
                            ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={loading}
                onClick={() => {
                  setLoading(true);
                  editCaseCreationWithupload({
                    id: caseData?.id,
                    ...formState,
                  })
                    .then(() => {
                      setLoading(false);
                      showTooltip("Performance AddedSuccessfully!", "success");
                      setFormState({
                        evaluation_date: "",
                        work_speed_evaluation: 0,
                        work_quality_evaluation: 0,
                        work_load_evaluation: 0,
                      });
                      onClose(true);
                    })
                    .catch((error) => {
                      setLoading(false);
                      showTooltip(
                        error?.response?.data?.detail ||
                          errorHandlerOnForm(error) ||
                          errorHandler(error) ||
                          "Something went wrong. Please try again.",
                        "error"
                      );
                    });
                }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon}
                    Loading...
                  </div>
                ) : (
                  "Evaluate"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PerformanceForm;
